import type * as babylon from "@babylonjs/core";
import lodash from "lodash";

import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import type * as schema from "@/shared/state/visualization/schema/v1/three-d";

import type { Articulation } from "../../../ModelLoader/articulation";
import { readJointsFromArticulation } from "../../../ModelLoader/readJoints";
import {
  withDefaultBindings,
  type ResolvedBinding,
} from "../../../ModelLoader/resolveDefaultBindings";
import * as components from "../../components";
import { BabylonState } from "../../state";

import { applyPointSizeOverride } from "./pointSizeOverride";

/**
 * Fetches geometry for any model-backed mesh whose load has gone stale.
 *
 * The procedural primitives are built inline by `updateMesh`; the two variants
 * that come out of a file need it fetched and parsed first, so only they appear
 * here. A `model_node` takes one node's geometry, a `model` a whole posable copy
 * of the file — both served from the loader's per-file template cache, so a
 * panel drawing one model from several entities fetches it once.
 *
 * A mesh transitions `stale → loading → loaded` once and is not returned to
 * `stale` by a failure, so a node name that matches nothing is attempted once
 * rather than re-fetched every 50ms.
 *
 * A `model`'s load is also where the panel learns the file's joints, since
 * picking a model commits only the file: see `recordModelJoints`.
 */
export function reloadStaleMeshes(state: BabylonState) {
  for (const entity of Object.values(state.entities)) {
    const mesh = entity.mesh;
    if (
      !mesh ||
      (mesh.spec.type !== "model_node" && mesh.spec.type !== "model") ||
      mesh.load?.state !== "stale"
    ) {
      continue;
    }
    // Cancel any load left over from a previous spec on this component.
    mesh.load.abortController.abort();

    mesh.load.state = "loading";
    const abortController = new AbortController();
    mesh.load.abortController = abortController;
    const { file } = mesh.spec;
    const label =
      mesh.spec.type === "model_node"
        ? `${file.relativePath ?? file.fileId}#${mesh.spec.nodeName}`
        : file.relativePath ?? file.fileId;

    const loading =
      mesh.spec.type === "model_node"
        ? loadNode(
            mesh,
            state,
            file.fileId,
            mesh.spec.nodeName,
            abortController.signal,
          )
        : loadModel(
            mesh,
            state,
            entity.id,
            file.fileId,
            abortController.signal,
          );

    loading.then(
      () => {
        if (!abortController.signal.aborted) {
          state.context.callbacks.onModelLoaded?.(label);
        }
      },
      (e: unknown) => {
        if (abortController.signal.aborted) {
          // Superseded or torn down, so whatever replaced this attempt owns
          // the component now. The error need not be an abort: the download
          // is shared through the loader's template, which this signal cannot
          // cancel, so it can still fail in the ordinary way after the entity
          // is gone.
          return;
        }
        const error = e instanceof Error ? e : new Error(String(e));
        mesh.load = { state: "loaded", abortController };
        mesh.error = error;
        state.context.callbacks.onModelLoadFailed(label, error);
        ErrorMonitoringService.captureError(error);
      },
    );
  }
}

/**
 * Attaches one node's geometry to the mesh component.
 *
 * Takes the component rather than reaching back through the state by entity id,
 * so a spec replaced during the async gap cannot have this load's result written
 * onto it — the same reasoning as `reloadPositions`.
 */
async function loadNode(
  mesh: components.Mesh,
  state: BabylonState,
  fileId: string,
  nodeName: string,
  signal: AbortSignal,
) {
  const clone = await state.context.modelLoader.cloneNode(
    fileId,
    nodeName,
    state.context.scene,
    signal,
  );

  if (signal.aborted) {
    // The abort landed while the asset was parsing. Nothing else holds this
    // clone, so release it or its geometry stays in the scene for good.
    clone.dispose();
    signal.throwIfAborted();
  }

  mesh.mesh = clone;
  markLoaded(mesh, state);
}

/**
 * Attaches a whole posable copy of the file to the mesh component.
 *
 * The copy's roots are parented to the component's anchor and the anchor alone
 * is placed, so the glTF-to-Babylon handedness conversion Babylon put on
 * `__root__` stays where it is. Writing TRS onto a root instead would replace
 * that conversion rather than compose with it, and lay the model on its side.
 *
 * `nodesByName` indexes the copy the way the file names it, taking the *first*
 * node of a repeated name — glTF does not require unique node names, and a joint
 * naming an ambiguous one can only be resolved arbitrarily. `__root__` is not
 * indexed: `getDescendants` starts below it, and it is Babylon's node rather
 * than one of the file's.
 */
async function loadModel(
  mesh: components.Mesh,
  state: BabylonState,
  entityId: schema.EntityID,
  fileId: string,
  signal: AbortSignal,
) {
  const instance = await state.context.modelLoader.instantiate(
    fileId,
    state.context.scene,
    signal,
  );

  const model = mesh.model;
  if (signal.aborted || !model) {
    // The abort landed while the asset was parsing, or the component was
    // replaced outright. Nothing else holds this copy, so release it or its
    // nodes stay in the scene for good.
    instance.dispose();
    signal.throwIfAborted();
    return;
  }

  model.instance = instance;
  model.nodesByName.clear();
  for (const root of instance.rootNodes) {
    root.parent = model.anchor;
    for (const node of root.getDescendants(false)) {
      if (!model.nodesByName.has(node.name)) {
        // Every node kind a model can hold — `Mesh`, `InstancedMesh`,
        // `TransformNode` — is a `TransformNode`; `getDescendants` is typed for
        // the general case and declares the looser `Node`.
        model.nodesByName.set(node.name, node as babylon.TransformNode);
      }
    }
  }

  // The loader's template carries the default; a spec's own size lives on
  // materials this copy owns. Applied from the *current* spec, which an
  // in-place reconciliation may have updated while the copy was loading.
  applyPointSizeOverride(
    model,
    mesh.spec.type === "model" ? mesh.spec.pointSize : undefined,
  );

  await recordModelJoints(entityId, mesh, instance.articulation, state, signal);
  markLoaded(mesh, state);
}

/**
 * Records the joints the loaded file declares, when the spec does not already.
 *
 * Picking a model commits the file alone, and a workspace saved before joints
 * were recorded has none either, so the first load of such a model is where
 * its joints are learned. They come off the same parse the copy did, so
 * learning them costs no extra fetch. Each joint the file gives a default
 * binding, and that nobody has decided about, is resolved against the
 * workspace's topics first. The model counts as loading until that is done,
 * so the panel's spinner covers the whole import.
 *
 * A model whose spec already records the file's joints, and holds values for
 * those joints alone, reports nothing, so reopening a workspace neither
 * rewrites them nor re-applies the file's defaults. A value for any other name
 * is always reported away: it binds nothing, yet its timeline would still be
 * fetched, and the settings list no joint to remove it from. A re-pick leaves
 * one behind when the same robot, converted again, no longer declares it.
 * Once the joints are recorded, that report drops the stray values and
 * nothing else. The file's defaults are applied once, at import, so a joint
 * the import left unbound stays that way.
 */
async function recordModelJoints(
  entityId: schema.EntityID,
  mesh: components.Mesh,
  articulation: Articulation | undefined,
  state: BabylonState,
  signal: AbortSignal,
) {
  const report = state.context.callbacks.onModelJointsRead;
  const spec = mesh.spec;
  if (!report || spec.type !== "model") {
    return;
  }
  const joints = readJointsFromArticulation(articulation);
  const recorded = lodash.isEqual(joints, spec.joints ?? []);
  const jointNames = new Set(joints.map((joint) => joint.name));
  if (
    recorded &&
    Object.keys(spec.jointValues ?? {}).every((key) => jointNames.has(key))
  ) {
    return;
  }

  // Only an import resolves defaults. A report for recorded joints is here to
  // drop their stray values.
  const undecided = recorded
    ? []
    : joints.filter(
        (joint) =>
          joint.defaultBinding !== undefined &&
          spec.jointValues?.[joint.name] === undefined,
      );
  const bound = await resolveDefaultBindingsFor(undecided, state, signal);
  signal.throwIfAborted();

  // The spec can be updated in place while the bindings resolve, by a point
  // size edited mid-load for one, so what was decided is read as it is now.
  const current = mesh.spec.type === "model" ? mesh.spec : spec;
  const jointValues = withDefaultBindings(joints, current.jointValues, bound);
  report(entityId, {
    file: current.file,
    joints,
    ...(jointValues ? { jointValues } : {}),
  });
}

/**
 * The defaults the workspace can supply for these joints.
 *
 * A failure to resolve them costs the model its declared defaults, never its
 * joints or its geometry, so it is captured rather than passed on. An abort is
 * passed on: the component was replaced, and nothing is reported for it.
 */
async function resolveDefaultBindingsFor(
  joints: readonly schema.Joint[],
  state: BabylonState,
  signal: AbortSignal,
): Promise<Record<string, ResolvedBinding>> {
  const resolve = state.context.resolveDefaultBindings;
  if (!resolve || joints.length === 0) {
    return {};
  }
  try {
    return (await resolve(joints, signal)).bound;
  } catch (e: unknown) {
    signal.throwIfAborted();
    ErrorMonitoringService.captureError(e);
    return {};
  }
}

/** Shared tail: the component is serviced, and the scene's extent has grown. */
function markLoaded(mesh: components.Mesh, state: BabylonState) {
  mesh.load = { state: "loaded", abortController: new AbortController() };
  delete mesh.error;

  // The scene has geometry it did not have before, so the cached third-person
  // zoom envelope no longer reflects its extent.
  state.sceneConfig.entityGeneration =
    (state.sceneConfig.entityGeneration ?? 0) + 1;
}
